package com.castly.admin.api

import com.castly.shared.dto.*
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.plugins.*
import io.ktor.client.request.*
import io.ktor.client.request.forms.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import io.ktor.client.statement.*
import kotlinx.browser.window
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import org.w3c.files.File
import org.w3c.files.get
import org.w3c.xhr.FormData
import org.w3c.xhr.XMLHttpRequest
import kotlinx.coroutines.await
import kotlinx.coroutines.suspendCancellableCoroutine
import org.w3c.fetch.RequestInit
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException
import kotlin.js.json

class SessionExpiredException : Exception("Your session has expired. Please log in again.")

/**
 * A non-2xx response, carrying the status so callers can branch on it.
 *
 * Notably lets a screen distinguish "you aren't allowed to see this" (403 — hide the
 * section) from a genuine failure (surface an error), without matching on message text.
 */
class ApiException(val status: Int, val body: String) :
    Exception("Request failed ($status): $body") {
    val isForbidden: Boolean get() = status == 403
}

class ApiClient(private val baseUrl: String) {
    @Serializable
    data class AdminUserDto(val sub: String? = null, val email: String? = null, val name: String? = null, val role: String? = null)

    @Serializable
    data class SendMagicLinkRequest(val email: String)

    @Serializable
    data class VerifyTokenRequest(val token: String)

    @Serializable
    data class AuthResponse(val token: String, val email: String, val role: String)

    @Serializable
    data class MessageResponse(val message: String)

    @Serializable
    data class UploadResponse(
        val url: String,
        val key: String,
        val fileName: String,
        val contentType: String,
        val size: Long
    )

    private val client = HttpClient {
        install(ContentNegotiation) {
            json(Json {
                prettyPrint = true
                isLenient = true
                ignoreUnknownKeys = true
            })
        }

        // Don't throw on non-2xx responses - we'll handle them manually
        expectSuccess = false
    }

    /**
     * Handle 401 responses by clearing tokens and redirecting to login
     */
    private fun handleSessionExpiration() {
        // Check if there was actually a token (avoid clearing during login flow)
        val hadToken = !window.localStorage.getItem("id_token").isNullOrBlank()

        if (hadToken) {
            // Clear expired token from localStorage
            window.localStorage.removeItem("id_token")
            window.localStorage.removeItem("user_role")

            // Set a flag in sessionStorage to show session expired message
            window.sessionStorage.setItem("session_expired", "true")

            // Log to console for debugging
            console.warn("Session expired (401). Token cleared. Please log in again.")

            // Reload the page to reset app state and show login
            window.location.reload()
        }

        // Throw specific exception that calling code can handle
        throw SessionExpiredException()
    }

    /**
     * Extension to check response status and handle 401
     */
    private suspend inline fun <reified T> HttpResponse.bodyOrHandle401(): T {
        if (status.value == 401) {
            handleSessionExpiration()
        }
        if (status.value !in 200..299) {
            throw ApiException(status.value, bodyAsText())
        }
        return body()
    }

    private fun HttpRequestBuilder.withAuth() {
        val token = window.localStorage.getItem("id_token")
        if (!token.isNullOrBlank()) {
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }

    /**
     * Get or generate a preview user ID for mobile preview.
     * Stored in localStorage under key "castly_preview_user_id".
     * Used as X-User-Id header for all v1 API requests from the preview.
     */
    fun getPreviewUserId(): String {
        val existing = window.localStorage.getItem("castly_preview_user_id")
        if (!existing.isNullOrBlank()) return existing
        val newId = generateUUID()
        window.localStorage.setItem("castly_preview_user_id", newId)
        return newId
    }

    /**
     * Set a specific preview user ID (for user switching feature).
     */
    fun setPreviewUserId(userId: String) {
        window.localStorage.setItem("castly_preview_user_id", userId)
    }

    private fun generateUUID(): String {
        return js("crypto.randomUUID()").unsafeCast<String>()
    }

    /**
     * Add X-User-Id header for preview API requests.
     */
    private fun HttpRequestBuilder.withPreviewUserId() {
        header("X-User-Id", getPreviewUserId())
    }

    /**
     * Helper function to check fetch responses for 401 and handle session expiration
     * Used by upload methods that use window.fetch directly instead of Ktor HttpClient
     */
    private fun handleFetchResponse(response: org.w3c.fetch.Response) {
        if (response.status.toInt() == 401) {
            // Check if there was actually a token
            val hadToken = !window.localStorage.getItem("id_token").isNullOrBlank()

            if (hadToken) {
                // Clear expired token from localStorage
                window.localStorage.removeItem("id_token")
                window.localStorage.removeItem("user_role")

                // Set a flag in sessionStorage to show session expired message
                window.sessionStorage.setItem("session_expired", "true")

                // Log to console for debugging
                console.warn("Session expired (401) during file upload. Token cleared. Please log in again.")

                // Reload the page to reset app state and show login
                window.location.reload()
            }
        }
    }

    suspend fun getStatus(): StatusDto {
        return client.get("$baseUrl/api/status").body()
    }

    suspend fun getMe(): AdminUserDto {
        return client.get("$baseUrl/api/admin/me") { withAuth() }.bodyOrHandle401()
    }

    suspend fun getDashboardStats(): DashboardStatsDto {
        return client.get("$baseUrl/api/admin/dashboard/stats") { withAuth() }.bodyOrHandle401()
    }

    suspend fun getAnalyticsOverview(): AnalyticsOverviewDto {
        return client.get("$baseUrl/api/admin/analytics/overview") { withAuth() }.bodyOrHandle401()
    }

    suspend fun getAnalyticsTrends(): AnalyticsTrendsDto {
        return client.get("$baseUrl/api/admin/analytics/trends") { withAuth() }.bodyOrHandle401()
    }

    suspend fun getAnalyticsTopContent(): AnalyticsTopContentDto {
        return client.get("$baseUrl/api/admin/analytics/top-content") { withAuth() }.bodyOrHandle401()
    }

    suspend fun getAnalyticsEngagement(): AnalyticsEngagementDto {
        return client.get("$baseUrl/api/admin/analytics/engagement") { withAuth() }.bodyOrHandle401()
    }

    suspend fun getAnalyticsRetention(): AnalyticsRetentionDto {
        return client.get("$baseUrl/api/admin/analytics/retention") { withAuth() }.bodyOrHandle401()
    }

    suspend fun getAnalyticsHeatmap(): AnalyticsHeatmapDto {
        return client.get("$baseUrl/api/admin/analytics/heatmap") { withAuth() }.bodyOrHandle401()
    }

    suspend fun getAnalyticsHealth(): AnalyticsHealthDto {
        return client.get("$baseUrl/api/admin/analytics/health") { withAuth() }.bodyOrHandle401()
    }

    @Serializable
    private data class ErrorResponse(val error: String)

    suspend fun sendMagicLink(email: String): MessageResponse {
        val response = client.post("$baseUrl/api/auth/magic-link") {
            contentType(ContentType.Application.Json)
            setBody(SendMagicLinkRequest(email))
        }
        if (!response.status.isSuccess()) {
            val err = response.body<ErrorResponse>()
            throw Exception(err.error)
        }
        return response.body()
    }

    suspend fun verifyMagicToken(token: String): AuthResponse {
        val response = client.post("$baseUrl/api/auth/verify") {
            contentType(ContentType.Application.Json)
            setBody(VerifyTokenRequest(token))
        }
        if (!response.status.isSuccess()) {
            val err = response.body<ErrorResponse>()
            throw Exception(err.error)
        }
        return response.body()
    }

    // Podcast API
    suspend fun listPodcasts(page: Int = 1, pageSize: Int = 20, query: String? = null, type: String? = null, tags: List<String>? = null, includeDeleted: Boolean = false): PodcastListResponse {
        return client.get("$baseUrl/api/admin/podcasts") {
            withAuth()
            parameter("page", page)
            parameter("pageSize", pageSize)
            if (!query.isNullOrBlank()) {
                parameter("q", query)
            }
            if (!type.isNullOrBlank()) {
                parameter("type", type)
            }
            if (!tags.isNullOrEmpty()) {
                parameter("tags", tags.joinToString(","))
            }
            if (includeDeleted) {
                parameter("includeDeleted", "true")
            }
        }.bodyOrHandle401()
    }

    suspend fun getPodcast(id: String): PodcastDto {
        return client.get("$baseUrl/api/admin/podcasts/$id") { withAuth() }.bodyOrHandle401()
    }

    suspend fun createPodcast(request: CreatePodcastRequest): PodcastDto {
        return client.post("$baseUrl/api/admin/podcasts") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    suspend fun updatePodcast(id: String, request: UpdatePodcastRequest): PodcastDto {
        return client.put("$baseUrl/api/admin/podcasts/$id") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    suspend fun deletePodcast(id: String) {
        client.delete("$baseUrl/api/admin/podcasts/$id") { withAuth() }
    }

    // Episode API
    @Serializable
    data class SeasonsResponse(val seasons: List<Int>)

    suspend fun getSeasons(podcastId: String): List<Int> {
        val response: SeasonsResponse = client.get("$baseUrl/api/admin/podcasts/$podcastId/seasons") {
            withAuth()
        }.bodyOrHandle401()
        return response.seasons
    }

    suspend fun listEpisodes(podcastId: String, page: Int = 1, pageSize: Int = 20, query: String? = null, season: Int? = null): EpisodeListResponse {
        return client.get("$baseUrl/api/admin/podcasts/$podcastId/episodes") {
            withAuth()
            parameter("page", page)
            parameter("pageSize", pageSize)
            if (!query.isNullOrBlank()) {
                parameter("q", query)
            }
            if (season != null) {
                parameter("season", season)
            }
        }.bodyOrHandle401()
    }

    suspend fun getEpisode(id: String): EpisodeDto {
        return client.get("$baseUrl/api/admin/episodes/$id") { withAuth() }.bodyOrHandle401()
    }

    suspend fun createEpisode(podcastId: String, request: CreateEpisodeRequest): EpisodeDto {
        return client.post("$baseUrl/api/admin/podcasts/$podcastId/episodes") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    suspend fun updateEpisode(id: String, request: UpdateEpisodeRequest): EpisodeDto {
        return client.put("$baseUrl/api/admin/episodes/$id") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    suspend fun deleteEpisode(id: String) {
        client.delete("$baseUrl/api/admin/episodes/$id") { withAuth() }
    }

    // Public API (for client preview - no auth required) - DEPRECATED
    // These methods are kept temporarily for backward compatibility
    // Mobile preview now uses /api/v1/* endpoints (see Feature 015 methods below)

    suspend fun getPublicPodcast(id: String): PodcastDto {
        return client.get("$baseUrl/api/public/podcasts/$id").body()
    }

    suspend fun getPublicSeasons(podcastId: String): List<Int> {
        val response: SeasonsResponse = client.get("$baseUrl/api/public/podcasts/$podcastId/seasons").body()
        return response.seasons
    }

    suspend fun listPublicEpisodes(podcastId: String, page: Int = 1, pageSize: Int = 20, query: String? = null, season: Int? = null): EpisodeListResponse {
        return client.get("$baseUrl/api/public/podcasts/$podcastId/episodes") {
            parameter("page", page)
            parameter("pageSize", pageSize)
            if (!query.isNullOrBlank()) {
                parameter("q", query)
            }
            if (season != null) {
                parameter("season", season)
            }
        }.body()
    }

    suspend fun getEpisodeStreamingUrl(episodeId: String): StreamingUrlResponse {
        return client.post("$baseUrl/api/public/episodes/$episodeId/stream") {
            contentType(ContentType.Application.Json)
        }.body()
    }

    // Linked Podcast API
    suspend fun previewRssFeed(rssUrl: String): List<RssPreviewResponse> {
        val response = client.post("$baseUrl/api/admin/linked-podcasts/preview") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(RssPreviewRequest(rssUrl))
        }
        if (!response.status.isSuccess()) {
            val err = response.body<ErrorResponse>()
            throw Exception(err.error)
        }
        return response.body()
    }

    suspend fun importFromRss(rssUrl: String, type: String? = null): List<PodcastDto> {
        val response = client.post("$baseUrl/api/admin/linked-podcasts/import") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(ImportRssRequest(rssUrl, type = type))
        }
        if (!response.status.isSuccess()) {
            val err = response.body<ErrorResponse>()
            throw Exception(err.error)
        }
        return response.body()
    }

    suspend fun syncLinkedPodcast(podcastId: String): SyncResultResponse {
        return client.post("$baseUrl/api/admin/linked-podcasts/$podcastId/sync") {
            withAuth()
        }.bodyOrHandle401()
    }

    suspend fun updateSyncSettings(podcastId: String, syncEnabled: Boolean?, syncIntervalMinutes: Int?): PodcastDto {
        return client.put("$baseUrl/api/admin/linked-podcasts/$podcastId/settings") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(SyncSettingsRequest(syncEnabled, syncIntervalMinutes))
        }.bodyOrHandle401()
    }

    suspend fun bulkPreviewRssFeeds(rssUrls: List<String>): BulkRssPreviewResponse {
        val response = client.post("$baseUrl/api/admin/linked-podcasts/preview/bulk") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(BulkRssPreviewRequest(rssUrls))
        }
        if (!response.status.isSuccess()) {
            val err = response.body<ErrorResponse>()
            throw Exception(err.error)
        }
        return response.body()
    }

    suspend fun bulkImportFromRss(imports: List<SingleImportRequest>): BulkRssImportResponse {
        val response = client.post("$baseUrl/api/admin/linked-podcasts/import/bulk") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(BulkRssImportRequest(imports))
        }
        if (!response.status.isSuccess()) {
            val err = response.body<ErrorResponse>()
            throw Exception(err.error)
        }
        return response.body()
    }

    // Media upload API
    suspend fun uploadEpisodeMedia(episodeId: String, file: File): UploadResponse {
        val token = window.localStorage.getItem("id_token")
        val formData = FormData()
        formData.append("file", file)

        val response = window.fetch(
            "$baseUrl/api/admin/media/episodes/$episodeId/upload",
            RequestInit(
                method = "POST",
                body = formData,
                headers = json("Authorization" to "Bearer $token")
            )
        ).await()

        if (!response.ok) {
            handleFetchResponse(response)
            val errorText = response.text().await()
            throw Exception("Upload failed: $errorText")
        }

        val jsonText = response.text().await()
        return Json.decodeFromString(jsonText)
    }

    suspend fun uploadPodcastCover(podcastId: String, file: File): UploadResponse {
        val token = window.localStorage.getItem("id_token")
        val formData = FormData()
        formData.append("file", file)

        val response = window.fetch(
            "$baseUrl/api/admin/media/podcasts/$podcastId/upload-cover",
            RequestInit(
                method = "POST",
                body = formData,
                headers = json("Authorization" to "Bearer $token")
            )
        ).await()

        if (!response.ok) {
            handleFetchResponse(response)
            val errorText = response.text().await()
            throw Exception("Upload failed: $errorText")
        }

        val jsonText = response.text().await()
        return Json.decodeFromString(jsonText)
    }

    suspend fun uploadEpisodeImage(episodeId: String, file: File): UploadResponse {
        val token = window.localStorage.getItem("id_token")
        val formData = FormData()
        formData.append("file", file)

        val response = window.fetch(
            "$baseUrl/api/admin/media/episodes/$episodeId/upload-image",
            RequestInit(
                method = "POST",
                body = formData,
                headers = json("Authorization" to "Bearer $token")
            )
        ).await()

        if (!response.ok) {
            handleFetchResponse(response)
            val errorText = response.text().await()
            throw Exception("Upload failed: $errorText")
        }

        val jsonText = response.text().await()
        return Json.decodeFromString(jsonText)
    }

    suspend fun updatePodcastCoverArt(podcastId: String, coverArtUrl: String): PodcastDto {
        return client.patch("$baseUrl/api/admin/podcasts/$podcastId/cover-art") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(mapOf("coverArtUrl" to coverArtUrl))
        }.bodyOrHandle401()
    }

    suspend fun removePodcastCoverArt(podcastId: String): PodcastDto {
        return client.delete("$baseUrl/api/admin/podcasts/$podcastId/cover-art") {
            withAuth()
        }.bodyOrHandle401()
    }

    suspend fun updateEpisodeImage(episodeId: String, imageUrl: String): EpisodeDto {
        return client.patch("$baseUrl/api/admin/episodes/$episodeId/image") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(mapOf("imageUrl" to imageUrl))
        }.bodyOrHandle401()
    }

    suspend fun removeEpisodeImage(episodeId: String): EpisodeDto {
        return client.delete("$baseUrl/api/admin/episodes/$episodeId/image") {
            withAuth()
        }.bodyOrHandle401()
    }

    // Tag API
    suspend fun listTags(q: String? = null, type: String? = null): List<TagWithCountDto> {
        return client.get("$baseUrl/api/admin/tags") {
            withAuth()
            if (!q.isNullOrBlank()) {
                parameter("q", q)
            }
            if (!type.isNullOrBlank()) {
                parameter("type", type)
            }
        }.bodyOrHandle401()
    }

    suspend fun listTypes(): List<PodcastTypeWithCountDto> {
        return client.get("$baseUrl/api/admin/podcasts/types") {
            withAuth()
        }.bodyOrHandle401()
    }

    suspend fun getPodcastTags(podcastId: String): List<TagDto> {
        return client.get("$baseUrl/api/admin/podcasts/$podcastId/tags") {
            withAuth()
        }.bodyOrHandle401()
    }

    suspend fun addPodcastTag(podcastId: String, request: AddTagRequest): TagDto {
        return client.post("$baseUrl/api/admin/podcasts/$podcastId/tags") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    suspend fun removePodcastTag(podcastId: String, tagId: String) {
        client.delete("$baseUrl/api/admin/podcasts/$podcastId/tags/$tagId") {
            withAuth()
        }
    }

    // Audiobook API
    suspend fun listAudiobooks(
        page: Int = 1,
        pageSize: Int = 20,
        query: String? = null,
        authorId: Int? = null,
        readerId: Int? = null,
        language: String? = null,
        tagIds: Set<String>? = null,
        includeDeleted: Boolean = false,
        labelId: Int? = null
    ): AudiobookListResponse {
        return client.get("$baseUrl/api/admin/audiobooks") {
            withAuth()
            parameter("page", page)
            parameter("pageSize", pageSize)
            if (!query.isNullOrBlank()) {
                parameter("q", query)
            }
            if (authorId != null) {
                parameter("authorId", authorId)
            }
            if (readerId != null) {
                parameter("readerId", readerId)
            }
            if (!language.isNullOrBlank()) {
                parameter("language", language)
            }
            if (!tagIds.isNullOrEmpty()) {
                parameter("tagIds", tagIds.joinToString(","))
            }
            if (includeDeleted) {
                parameter("includeDeleted", true)
            }
            if (labelId != null) {
                parameter("labelId", labelId)
            }
        }.bodyOrHandle401()
    }

    suspend fun previewCsvImport(file: File): CsvPreviewResponse {
        val token = window.localStorage.getItem("id_token")
        val formData = FormData()
        formData.append("file", file)

        val response = window.fetch(
            "$baseUrl/api/admin/audiobooks/import/csv/preview",
            RequestInit(
                method = "POST",
                body = formData,
                headers = json("Authorization" to "Bearer $token")
            )
        ).await()

        if (!response.ok) {
            handleFetchResponse(response)
            val errorText = response.text().await()
            throw Exception("CSV preview failed: $errorText")
        }

        val jsonText = response.text().await()
        return Json.decodeFromString(jsonText)
    }

    suspend fun importAudiobooksFromCsv(file: File, labelId: Int? = null, skipExisting: Boolean = true): ImportReportDto {
        val token = window.localStorage.getItem("id_token")
        val formData = FormData()
        formData.append("file", file)

        val params = buildList {
            if (labelId != null) add("labelId=$labelId")
            if (!skipExisting) add("skipExisting=false")
        }.joinToString("&").let { if (it.isNotBlank()) "?$it" else "" }
        val response = window.fetch(
            "$baseUrl/api/admin/audiobooks/import/csv$params",
            RequestInit(
                method = "POST",
                body = formData,
                headers = json("Authorization" to "Bearer $token")
            )
        ).await()

        if (!response.ok) {
            handleFetchResponse(response)
            val errorText = response.text().await()
            throw Exception("CSV import failed: $errorText")
        }

        val jsonText = response.text().await()
        return Json.decodeFromString(jsonText)
    }

    suspend fun syncReadersFromCsv(file: File): SyncReportDto {
        val token = window.localStorage.getItem("id_token")
        val formData = FormData()
        formData.append("file", file)

        val response = window.fetch(
            "$baseUrl/api/admin/audiobooks/sync-readers/csv",
            RequestInit(
                method = "POST",
                body = formData,
                headers = json("Authorization" to "Bearer $token")
            )
        ).await()

        if (!response.ok) {
            handleFetchResponse(response)
            val errorText = response.text().await()
            throw Exception("Reader sync failed: $errorText")
        }

        val jsonText = response.text().await()
        return Json.decodeFromString(jsonText)
    }

    suspend fun getAudiobook(audiobookId: String): AudiobookDto {
        return client.get("$baseUrl/api/admin/audiobooks/$audiobookId") { withAuth() }.bodyOrHandle401()
    }

    suspend fun createAudiobook(request: CreateAudiobookRequest): AudiobookDto {
        return client.post("$baseUrl/api/admin/audiobooks") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    suspend fun updateAudiobook(audiobookId: String, request: UpdateAudiobookRequest): AudiobookDto {
        return client.put("$baseUrl/api/admin/audiobooks/$audiobookId") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    suspend fun deleteAudiobook(audiobookId: String) {
        client.delete("$baseUrl/api/admin/audiobooks/$audiobookId") { withAuth() }
    }

    suspend fun hardDeleteAudiobook(audiobookId: String) {
        client.delete("$baseUrl/api/admin/audiobooks/$audiobookId/permanent") { withAuth() }
    }

    suspend fun importEpisodesFromFolder(
        audiobookId: String,
        files: org.w3c.files.FileList,
        onProgress: ((fraction: Float) -> Unit)? = null
    ): ImportReportDto {
        val formData = FormData()
        for (i in 0 until files.length) {
            val file = files[i]
            if (file != null) {
                formData.append("files", file, file.name)
            }
        }
        val jsonText = uploadWithProgress(
            url = "$baseUrl/api/admin/audiobooks/$audiobookId/episodes/import",
            method = "POST",
            formData = formData,
            onProgress = { loaded, total -> onProgress?.invoke((loaded / total).toFloat()) }
        )
        return Json.decodeFromString(jsonText)
    }

    suspend fun importSingleEpisodeFile(
        audiobookId: String,
        file: org.w3c.files.File,
        onProgress: ((fraction: Float) -> Unit)? = null
    ): ImportReportDto {
        val formData = FormData()
        formData.append("files", file, file.name)
        val jsonText = uploadWithProgress(
            url = "$baseUrl/api/admin/audiobooks/$audiobookId/episodes/import",
            method = "POST",
            formData = formData,
            onProgress = { loaded, total -> onProgress?.invoke((loaded / total).toFloat()) }
        )
        return Json.decodeFromString(jsonText)
    }

    suspend fun bulkImportEpisodes(
        files: org.w3c.files.FileList,
        onProgress: ((fraction: Float) -> Unit)? = null
    ): BulkEpisodeImportResponse {
        val formData = FormData()
        for (i in 0 until files.length) {
            val file = files[i]
            if (file != null) {
                formData.append("files", file, file.name)
            }
        }
        val jsonText = uploadWithProgress(
            url = "$baseUrl/api/admin/audiobooks/episodes/bulk-import",
            method = "POST",
            formData = formData,
            onProgress = { loaded, total -> onProgress?.invoke((loaded / total).toFloat()) }
        )
        return Json.decodeFromString(jsonText)
    }

    suspend fun bulkImportEpisodes(
        files: List<File>,
        onProgress: ((fraction: Float) -> Unit)? = null
    ): BulkEpisodeImportResponse {
        val formData = FormData()
        files.forEach { file ->
            formData.append("files", file, file.name)
        }
        val jsonText = uploadWithProgress(
            url = "$baseUrl/api/admin/audiobooks/episodes/bulk-import",
            method = "POST",
            formData = formData,
            onProgress = { loaded, total -> onProgress?.invoke((loaded / total).toFloat()) }
        )
        return Json.decodeFromString(jsonText)
    }

    suspend fun getAudiobookEpisodes(audiobookId: String): AudiobookEpisodesResponse {
        return client.get("$baseUrl/api/admin/audiobooks/$audiobookId/episodes") { withAuth() }.bodyOrHandle401()
    }

    // Audiobook Tag Management
    suspend fun listAudiobookTags(q: String? = null, labelId: Int? = null): List<TagWithCountDto> {
        return client.get("$baseUrl/api/admin/audiobooks/tags/with-counts") {
            withAuth()
            if (!q.isNullOrBlank()) {
                parameter("q", q)
            }
            if (labelId != null) {
                parameter("labelId", labelId)
            }
        }.bodyOrHandle401()
    }

    suspend fun getAudiobookTags(audiobookId: String): List<TagDto> {
        return client.get("$baseUrl/api/admin/audiobooks/$audiobookId/tags") {
            withAuth()
        }.bodyOrHandle401()
    }

    suspend fun addAudiobookTag(audiobookId: String, request: AddTagRequest): List<TagDto> {
        return client.post("$baseUrl/api/admin/audiobooks/$audiobookId/tags") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    suspend fun removeAudiobookTag(audiobookId: String, tagId: String) {
        client.delete("$baseUrl/api/admin/audiobooks/$audiobookId/tags/$tagId") {
            withAuth()
        }
    }

    // Audiobook Episode Edit API (Feature 018)

    suspend fun getAudiobookEpisodeDetail(audiobookId: String, episodeId: String): AdminEpisodeDetailDto {
        return client.get("$baseUrl/api/admin/audiobooks/$audiobookId/episodes/$episodeId") {
            withAuth()
        }.bodyOrHandle401()
    }

    suspend fun updateAudiobookEpisodeMetadata(audiobookId: String, episodeId: String, request: UpdateAudiobookEpisodeRequest): AdminEpisodeDetailDto {
        return client.patch("$baseUrl/api/admin/audiobooks/$audiobookId/episodes/$episodeId") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    suspend fun upsertEpisodeTranslation(audiobookId: String, episodeId: String, lang: String, request: UpsertEpisodeTranslationRequest): EpisodeTranslationDto {
        return client.put("$baseUrl/api/admin/audiobooks/$audiobookId/episodes/$episodeId/translations/$lang") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    suspend fun deleteEpisodeTranslation(audiobookId: String, episodeId: String, lang: String) {
        client.delete("$baseUrl/api/admin/audiobooks/$audiobookId/episodes/$episodeId/translations/$lang") {
            withAuth()
        }
    }

    suspend fun uploadAudiobookEpisodeImage(audiobookId: String, episodeId: String, file: File): Map<String, String> {
        val token = window.localStorage.getItem("id_token")
        val formData = FormData()
        formData.append("image", file)
        val response = window.fetch(
            "$baseUrl/api/admin/audiobooks/$audiobookId/episodes/$episodeId/image",
            RequestInit(method = "PUT", body = formData, headers = json("Authorization" to "Bearer $token"))
        ).await()
        if (!response.ok) {
            handleFetchResponse(response)
            throw Exception("Upload failed: ${response.text().await()}")
        }
        val jsonText = response.text().await()
        return Json.decodeFromString(jsonText)
    }

    suspend fun removeAudiobookEpisodeImage(audiobookId: String, episodeId: String) {
        client.delete("$baseUrl/api/admin/audiobooks/$audiobookId/episodes/$episodeId/image") {
            withAuth()
        }
    }

    suspend fun softDeleteAudiobookEpisode(audiobookId: String, episodeId: String) {
        client.delete("$baseUrl/api/admin/audiobooks/$audiobookId/episodes/$episodeId") {
            withAuth()
        }
    }

    suspend fun restoreAudiobookEpisode(audiobookId: String, episodeId: String): AdminEpisodeDetailDto {
        return client.post("$baseUrl/api/admin/audiobooks/$audiobookId/episodes/$episodeId/restore") {
            withAuth()
        }.bodyOrHandle401()
    }

    suspend fun replaceAudiobookEpisodeAudio(audiobookId: String, episodeId: String, file: File): AdminEpisodeDetailDto {
        val token = window.localStorage.getItem("id_token")
        val formData = FormData()
        formData.append("audio", file)
        formData.append("confirm", "replace")
        val response = window.fetch(
            "$baseUrl/api/admin/audiobooks/$audiobookId/episodes/$episodeId/audio",
            RequestInit(method = "PUT", body = formData, headers = json("Authorization" to "Bearer $token"))
        ).await()
        if (!response.ok) {
            handleFetchResponse(response)
            throw Exception("Audio replacement failed: ${response.text().await()}")
        }
        val jsonText = response.text().await()
        return Json.decodeFromString(jsonText)
    }

    suspend fun uploadAudiobookCoverImage(audiobookId: String, file: File): Map<String, String> {
        val token = window.localStorage.getItem("id_token")
        val formData = FormData()
        formData.append("image", file)
        val response = window.fetch(
            "$baseUrl/api/admin/audiobooks/$audiobookId/cover-image",
            RequestInit(method = "PUT", body = formData, headers = json("Authorization" to "Bearer $token"))
        ).await()
        if (!response.ok) {
            handleFetchResponse(response)
            throw Exception("Cover upload failed: ${response.text().await()}")
        }
        val jsonText = response.text().await()
        return Json.decodeFromString(jsonText)
    }

    // Feature 026: Upload single episode audio file for bulk upload
    suspend fun uploadEpisodeFile(
        audiobookId: String,
        file: org.w3c.files.File,
        onProgress: ((fraction: Float) -> Unit)? = null
    ): EpisodeUploadResult {
        val formData = FormData()
        formData.append("file", file, file.name)
        val jsonText = uploadWithProgress(
            url = "$baseUrl/api/admin/audiobooks/$audiobookId/episodes/upload",
            method = "POST",
            formData = formData,
            onProgress = { loaded, total -> onProgress?.invoke((loaded / total).toFloat()) }
        )
        return Json.decodeFromString(jsonText)
    }

    // Feature 026: Bulk create episodes from uploaded files
    suspend fun bulkCreateEpisodes(audiobookId: String, request: BulkCreateEpisodesRequest): BulkCreateEpisodesResponse {
        return client.post("$baseUrl/api/admin/audiobooks/$audiobookId/episodes/bulk-create") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    // Feature 026: Publish audiobook (DRAFT -> PUBLISHED)
    suspend fun publishAudiobook(audiobookId: String): AudiobookDto {
        return client.put("$baseUrl/api/admin/audiobooks/$audiobookId/publish") {
            withAuth()
        }.bodyOrHandle401()
    }

    // Feature 021: Recalculate episode durations from MP3 files
    suspend fun recalculateDurations(): Map<String, String> {
        return client.post("$baseUrl/api/admin/audiobooks/recalculate-durations") {
            withAuth()
        }.bodyOrHandle401()
    }

    // Recalculate episode durations for a specific audiobook (SUPER_ADMIN only)
    suspend fun recalculateAudiobookDurations(audiobookId: String): Map<String, String> {
        return client.post("$baseUrl/api/admin/audiobooks/$audiobookId/recalculate-durations") {
            withAuth()
        }.bodyOrHandle401()
    }

    // Feature 021: Restore a soft-deleted audiobook
    suspend fun restoreAudiobook(id: String): Map<String, String> {
        return client.put("$baseUrl/api/admin/audiobooks/restore/$id") {
            withAuth()
        }.bodyOrHandle401()
    }

    // Feature 021: Hide all audiobooks
    suspend fun hideAllAudiobooks(): Map<String, Any> {
        return client.put("$baseUrl/api/admin/audiobooks/hide-all") {
            withAuth()
        }.bodyOrHandle401()
    }

    suspend fun restorePodcast(id: String): PodcastDto {
        return client.post("$baseUrl/api/admin/podcasts/$id/restore") {
            withAuth()
        }.bodyOrHandle401()
    }

    suspend fun uploadPodcastCoverImageFile(id: String, file: File): Map<String, String> {
        val token = window.localStorage.getItem("id_token")
        val formData = FormData()
        formData.append("image", file)
        val response = window.fetch(
            "$baseUrl/api/admin/podcasts/$id/cover-image",
            RequestInit(method = "PUT", body = formData, headers = json("Authorization" to "Bearer $token"))
        ).await()
        if (!response.ok) {
            handleFetchResponse(response)
            throw Exception("Cover upload failed: ${response.text().await()}")
        }
        val jsonText = response.text().await()
        return Json.decodeFromString(jsonText)
    }

    // Author API
    suspend fun listAuthors(page: Int = 1, pageSize: Int = 20, query: String? = null): AuthorListResponse {
        return client.get("$baseUrl/api/admin/authors") {
            withAuth()
            parameter("page", page)
            parameter("pageSize", pageSize)
            if (!query.isNullOrBlank()) {
                parameter("q", query)
            }
        }.bodyOrHandle401()
    }

    suspend fun getAuthor(id: Int): AuthorDetailResponse {
        return client.get("$baseUrl/api/admin/authors/$id") {
            withAuth()
        }.bodyOrHandle401()
    }

    suspend fun createAuthor(request: CreateAuthorRequest): AuthorDto {
        return client.post("$baseUrl/api/admin/authors") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    suspend fun updateAuthor(id: Int, request: UpdateAuthorRequest): AuthorDto {
        return client.put("$baseUrl/api/admin/authors/$id") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    suspend fun deleteAuthor(id: Int) {
        client.delete("$baseUrl/api/admin/authors/$id") {
            withAuth()
        }
    }

    suspend fun mergeAuthors(request: MergeAuthorRequest): AuthorDto {
        return client.post("$baseUrl/api/admin/authors/merge") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    // Reader API
    suspend fun listReaders(page: Int = 1, pageSize: Int = 20, query: String? = null): ReaderListResponse {
        return client.get("$baseUrl/api/admin/readers") {
            withAuth()
            parameter("page", page)
            parameter("pageSize", pageSize)
            if (!query.isNullOrBlank()) {
                parameter("q", query)
            }
        }.bodyOrHandle401()
    }

    suspend fun getReader(id: Int): ReaderDetailResponse {
        return client.get("$baseUrl/api/admin/readers/$id") {
            withAuth()
        }.bodyOrHandle401()
    }

    suspend fun createReader(request: CreateReaderRequest): ReaderDto {
        return client.post("$baseUrl/api/admin/readers") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    suspend fun updateReader(id: Int, request: UpdateReaderRequest): ReaderDto {
        return client.put("$baseUrl/api/admin/readers/$id") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    suspend fun deleteReader(id: Int) {
        client.delete("$baseUrl/api/admin/readers/$id") {
            withAuth()
        }
    }

    suspend fun mergeReaders(request: MergeReaderRequest): ReaderDto {
        return client.post("$baseUrl/api/admin/readers/merge") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    suspend fun getTag(id: String): TagDto {
        return client.get("$baseUrl/api/admin/tags/$id") {
            withAuth()
        }.bodyOrHandle401()
    }

    suspend fun createTag(request: CreateTagRequest): TagDto {
        return client.post("$baseUrl/api/admin/tags") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    suspend fun updateTag(id: String, request: UpdateTagRequest): TagDto {
        return client.put("$baseUrl/api/admin/tags/$id") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    suspend fun deleteTag(id: String) {
        client.delete("$baseUrl/api/admin/tags/$id") {
            withAuth()
        }
    }

    suspend fun mergeTags(request: MergeTagRequest): TagDto {
        return client.post("$baseUrl/api/admin/tags/merge") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    // Audit Log API

    /**
     * Fetch a page of audit log entries. Caller role is enforced on the backend.
     * [page] is 0-indexed (page=0 → first page).
     */
    suspend fun listAuditLog(
        page: Int = 0,
        pageSize: Int = 20,
        actionType: String? = null,
        entityType: String? = null,
        keyword: String? = null,
        dateFrom: String? = null,
        dateTo: String? = null,
        actorId: String? = null,
        labelName: String? = null
    ): com.castly.shared.dto.AuditLogPageDto {
        return client.get("$baseUrl/api/admin/audit-log") {
            withAuth()
            parameter("page", page)
            parameter("pageSize", pageSize)
            if (!actionType.isNullOrBlank()) parameter("actionType", actionType)
            if (!entityType.isNullOrBlank()) parameter("entityType", entityType)
            if (!keyword.isNullOrBlank()) parameter("keyword", keyword)
            if (!dateFrom.isNullOrBlank()) parameter("dateFrom", dateFrom)
            if (!dateTo.isNullOrBlank()) parameter("dateTo", dateTo)
            if (!actorId.isNullOrBlank()) parameter("actorId", actorId)
            if (!labelName.isNullOrBlank()) parameter("labelName", labelName)
        }.bodyOrHandle401()
    }

    /** Fetch distinct actors for the actor filter dropdown (ADMIN only). */
    suspend fun listAuditLogActors(): List<com.castly.shared.dto.AuditActorDto> {
        return client.get("$baseUrl/api/admin/audit-log/actors") { withAuth() }.bodyOrHandle401()
    }

    /** Fetch a single audit log entry by ID with full change_payload. */
    suspend fun getAuditLogEntry(id: String): com.castly.shared.dto.AuditLogEntryDto {
        return client.get("$baseUrl/api/admin/audit-log/$id") { withAuth() }.bodyOrHandle401()
    }

    /**
     * Fetch audit log entries as CSV text using the current filter state.
     * Uses standard Bearer auth header — same as all other API calls.
     * Returns the raw CSV string; caller is responsible for triggering the browser download.
     */
    suspend fun exportAuditLog(
        actionType: String? = null,
        entityType: String? = null,
        keyword: String? = null,
        dateFrom: String? = null,
        dateTo: String? = null
    ): String {
        val response = client.get("$baseUrl/api/admin/audit-log/export") {
            withAuth()
            if (!actionType.isNullOrBlank()) parameter("actionType", actionType)
            if (!entityType.isNullOrBlank()) parameter("entityType", entityType)
            if (!keyword.isNullOrBlank()) parameter("keyword", keyword)
            if (!dateFrom.isNullOrBlank()) parameter("dateFrom", dateFrom)
            if (!dateTo.isNullOrBlank()) parameter("dateTo", dateTo)
        }
        if (response.status.value == 401) handleSessionExpiration()
        if (!response.status.isSuccess()) {
            throw Exception("Export failed (${response.status.value})")
        }
        return response.bodyAsText()
    }

    // Feature 015: Mobile Preview API Compatibility - Updated /api/v1 endpoints

    /**
     * ApiResult sealed class for error handling (T009)
     * Provides structured success/error states with retry support
     */
    sealed class ApiResult<out T> {
        data class Success<T>(val data: T) : ApiResult<T>()
        data class Error(
            val code: String,
            val message: String,
            val details: Map<String, String>? = null
        ) : ApiResult<Nothing>()
    }

    /**
     * Convert UI language code to ISO 639-3 (T010)
     * Maps "en" → "eng", "ar" → "ara"
     */
    private fun languageToIso639_3(language: String): String = when (language) {
        "en" -> "eng"
        "ar" -> "ara"
        else -> "eng" // Default to English
    }

    /**
     * Safe API call wrapper with error handling (T011, T012)
     * Catches ClientRequestException and logs 4xx/5xx errors to console
     */
    private suspend fun <T> safeApiCall(block: suspend () -> T): ApiResult<T> {
        return try {
            val result = block()
            ApiResult.Success(result)
        } catch (e: ClientRequestException) {
            // Log 4xx/5xx errors to console (FR-018)
            console.error("API Error ${e.response.status.value}: ${e.message}")

            // Try to parse ErrorResponse from backend
            val errorResponse = try {
                e.response.body<com.castly.admin.dto.ErrorResponse>()
            } catch (parseError: Exception) {
                // If parsing fails, create generic error
                com.castly.admin.dto.ErrorResponse(
                    error = "HTTP_${e.response.status.value}",
                    message = e.message ?: "API request failed",
                    details = null
                )
            }

            ApiResult.Error(
                code = errorResponse.error,
                message = errorResponse.message,
                details = errorResponse.details
            )
        } catch (e: Exception) {
            console.error("Unexpected error: ${e.message}")
            ApiResult.Error(
                code = "UNEXPECTED_ERROR",
                message = e.message ?: "An unexpected error occurred",
                details = null
            )
        }
    }

    /**
     * Get paginated list of content (podcasts and/or audiobooks)
     *
     * Fetches content from the unified `/api/v1/content` endpoint with support for
     * filtering by type, text search, and multilingual content retrieval.
     *
     * **Pagination**: Uses page-based pagination (not offset-based)
     * - Page 1 = items 1-20
     * - Page 2 = items 21-40
     * - etc.
     *
     * **Performance**: Optimized with database-level pagination and batch loading.
     * Typical response time: 150-250ms for 20 items.
     *
     * @param type Content type filter: "podcast", "audiobook", or null for all types
     * @param language UI language code: "en" (English) or "ar" (Arabic). Automatically converted to ISO 639-3.
     * @param page Page number (1-indexed). Default: 1
     * @param pageSize Number of items per page (1-50). Default: 20
     * @param query Optional text search query. When provided, uses `/api/v1/search` endpoint instead.
     * @return ApiResult containing ContentListResponse with items, total count, page, and pageSize
     *
     * @see searchContent For advanced filtering with tags and video
     * @see getContentById For retrieving specific content by UUID
     */
    suspend fun getContent(
        type: String? = null,
        language: String = "en",
        page: Int = 1,
        pageSize: Int = 20,
        query: String? = null,
        hasVideo: Boolean? = null
    ): ApiResult<com.castly.admin.dto.ContentListResponse> = safeApiCall {
        val response = if (!query.isNullOrBlank()) {
            client.get("$baseUrl/api/v1/search") {
                withPreviewUserId()
                header("Accept-Language", languageToIso639_3(language))
                parameter("page", page)
                parameter("pageSize", pageSize)
                parameter("q", query)
                if (type != null) {
                    parameter("type", type)
                }
                if (hasVideo != null) {
                    parameter("hasVideo", hasVideo)
                }
            }
        } else {
            client.get("$baseUrl/api/v1/content") {
                withPreviewUserId()
                header("Accept-Language", languageToIso639_3(language))
                parameter("page", page)
                parameter("pageSize", pageSize)
                if (type != null) {
                    parameter("type", type)
                }
                if (hasVideo != null) {
                    parameter("hasVideo", hasVideo)
                }
            }
        }
        if (!response.status.isSuccess()) {
            val errorBody = try {
                response.body<com.castly.admin.dto.ErrorResponse>()
            } catch (_: Exception) {
                com.castly.admin.dto.ErrorResponse(
                    error = "HTTP_${response.status.value}",
                    message = "Request failed with status ${response.status.value}",
                    details = null
                )
            }
            throw Exception("${errorBody.error}: ${errorBody.message}")
        }
        response.body()
    }

    /**
     * Get content details by UUID
     *
     * Retrieves detailed information for a specific podcast or audiobook using its unique identifier.
     * Returns unified ContentResponse structure for both content types.
     *
     * @param contentId UUID of the content (RFC 4122 format)
     * @param language UI language code: "en" (English) or "ar" (Arabic)
     * @return ApiResult containing ContentResponse with full content details, creators, tags, and episode count
     *
     * @see getContent For listing multiple content items
     * @see getContentEpisodes For retrieving episodes of this content
     */
    suspend fun getContentById(
        contentId: String,
        language: String = "en"
    ): ApiResult<com.castly.admin.dto.ContentResponse> = safeApiCall {
        val response = client.get("$baseUrl/api/v1/content/$contentId") {
            withPreviewUserId()
            header("Accept-Language", languageToIso639_3(language))
        }
        if (!response.status.isSuccess()) {
            val errorBody = try {
                response.body<com.castly.admin.dto.ErrorResponse>()
            } catch (_: Exception) {
                com.castly.admin.dto.ErrorResponse(
                    error = "HTTP_${response.status.value}",
                    message = "Request failed with status ${response.status.value}",
                    details = null
                )
            }
            throw Exception("${errorBody.error}: ${errorBody.message}")
        }
        response.body()
    }

    /**
     * Get episodes for content (podcast or audiobook)
     *
     * Retrieves a paginated list of episodes/chapters for a specific podcast or audiobook.
     * Episodes are returned in sequential order (by episode number).
     *
     * **Pagination**: Uses page-based pagination
     * - Recommended pageSize: 20 for mobile preview
     * - Maximum pageSize: 50
     *
     * @param contentId UUID of the parent content (podcast or audiobook)
     * @param language UI language code: "en" (English) or "ar" (Arabic)
     * @param page Page number (1-indexed). Default: 1
     * @param pageSize Number of episodes per page (1-50). Default: 20
     * @return ApiResult containing list of EpisodeResponse objects (NOTE: Returns list directly, not paginated response object)
     *
     * @param sortBy Optional sort field: "releaseDate" or "title" (null = backend default order)
     * @param sortDirection Optional "ASC" or "DESC" (only sent when non-null)
     *
     * @see getEpisodeById For retrieving specific episode details
     * @see getEpisodeStreamingUrlV1 For getting playback URL
     */
    suspend fun getContentEpisodes(
        contentId: String,
        language: String = "en",
        page: Int = 1,
        pageSize: Int = 20,
        sortBy: String? = null,
        sortDirection: String? = null
    ): ApiResult<List<com.castly.admin.dto.EpisodeResponse>> = safeApiCall {
        // Backend returns paginated response, extract items
        val httpResponse = client.get("$baseUrl/api/v1/content/$contentId/episodes") {
            withPreviewUserId()
            header("Accept-Language", languageToIso639_3(language))
            parameter("page", page)
            parameter("pageSize", pageSize)
            if (sortBy != null) parameter("sortBy", sortBy)
            if (sortDirection != null) parameter("sortDirection", sortDirection)
        }
        if (!httpResponse.status.isSuccess()) {
            val errorBody = try {
                httpResponse.body<com.castly.admin.dto.ErrorResponse>()
            } catch (_: Exception) {
                com.castly.admin.dto.ErrorResponse(
                    error = "HTTP_${httpResponse.status.value}",
                    message = "Request failed with status ${httpResponse.status.value}",
                    details = null
                )
            }
            throw Exception("${errorBody.error}: ${errorBody.message}")
        }
        val response: com.castly.admin.dto.EpisodeListResponse = httpResponse.body()
        response.items
    }

    /**
     * Get episode details by UUID
     *
     * Retrieves detailed information for a specific episode/chapter, including parent content context.
     * Response includes contentId and contentType for easy navigation back to parent content.
     *
     * @param episodeId UUID of the episode
     * @param language UI language code: "en" (English) or "ar" (Arabic)
     * @return ApiResult containing EpisodeResponse with episode details, duration, publish date, and optional video info
     *
     * @see getContentEpisodes For listing all episodes of a content
     * @see getEpisodeStreamingUrlV1 For getting playback URL
     */
    suspend fun getEpisodeById(
        episodeId: String,
        language: String = "en"
    ): ApiResult<com.castly.admin.dto.EpisodeResponse> = safeApiCall {
        client.get("$baseUrl/api/v1/episodes/$episodeId") {
            withPreviewUserId()
            header("Accept-Language", languageToIso639_3(language))
        }.body()
    }

    /**
     * Get streaming URL for episode
     *
     * Retrieves a time-limited, signed CloudFront URL for audio playback.
     * URLs are IP-restricted and expire after the specified duration.
     *
     * **Security**: URLs are never stored in API responses - must be requested when needed
     * **Expiration**: URLs typically expire in 30-60 minutes
     * **Refresh**: Client should request new URL before expiration
     *
     * @param episodeId UUID of the episode to stream
     * @return ApiResult containing StreamingUrlResponse with signed URL and expiration time
     *
     * @see getEpisodeById For episode metadata before streaming
     */
    suspend fun getEpisodeStreamingUrlV1(
        episodeId: String
    ): ApiResult<com.castly.admin.dto.StreamingUrlResponse> = safeApiCall {
        client.get("$baseUrl/api/v1/episodes/$episodeId/stream") {
            withPreviewUserId()
        }.body()
    }

    /**
     * Get all tags with multilingual names
     *
     * Retrieves the complete list of content tags available in the system.
     * Tag names are localized based on the requested language.
     *
     * **Use Cases**:
     * - Populate tag filter dropdowns
     * - Display tag clouds
     * - Content categorization UI
     *
     * @param language UI language code: "en" (English) or "ar" (Arabic)
     * @return ApiResult containing list of TagResponse objects with UUID and localized name
     *
     * @see searchContent For filtering content by tags
     */
    suspend fun getTags(
        language: String = "en"
    ): ApiResult<List<com.castly.admin.dto.TagResponse>> = safeApiCall {
        val response: com.castly.admin.dto.TagsResponse = client.get("$baseUrl/api/v1/tags") {
            withPreviewUserId()
            header("Accept-Language", languageToIso639_3(language))
        }.bodyOrHandle401()
        response.tags
    }

    /**
     * Get home page with all active sections
     *
     * Fetches the curated home page including carousels, banners, and static sections.
     * Sections are returned in display order.
     *
     * @param language ISO 639-3 language code: "eng" (English) or "ara" (Arabic)
     * @return HomePageResponse containing all active sections with items
     */
    suspend fun getHomePage(
        language: String = "eng"
    ): ApiResult<HomePageResponse> = safeApiCall {
        client.get("$baseUrl/api/v1/home") {
            withPreviewUserId()
            parameter("language", language)
        }.bodyOrHandle401()
    }

    /**
     * Search content by text query, tags, and/or video filter
     *
     * Advanced content search endpoint supporting multiple filter criteria.
     * All filters are combined with AND logic.
     *
     * **Filter Logic**:
     * - Multiple tags: Content must have ALL specified tags (AND logic)
     * - Text query: Searches in title and description fields
     * - Video filter: Includes only content with video episodes
     *
     * **Pagination**: Uses page-based pagination (1-indexed)
     *
     * **Examples**:
     * ```kotlin
     * // Search for podcasts about "technology" with tags "AI" and "News"
     * searchContent(
     *     query = "technology",
     *     tagIds = listOf("uuid1", "uuid2"),
     *     language = "en",
     *     page = 1,
     *     pageSize = 20
     * )
     * ```
     *
     * @param query Optional text search query (searches title and description)
     * @param tagIds Optional list of tag UUIDs. Content must have ALL tags (AND logic).
     * @param hasVideo Optional video filter. When true, returns only content with video episodes.
     * @param language UI language code: "en" (English) or "ar" (Arabic)
     * @param page Page number (1-indexed). Default: 1
     * @param pageSize Number of items per page (1-50). Default: 20
     * @return ApiResult containing ContentListResponse with matching items
     *
     * @see getContent For simple listing without advanced filters
     * @see getTags For retrieving available tags
     */
    suspend fun searchContent(
        query: String? = null,
        tagIds: List<String>? = null,
        hasVideo: Boolean? = null,
        language: String = "en",
        page: Int = 1,
        pageSize: Int = 20
    ): ApiResult<com.castly.admin.dto.ContentListResponse> = safeApiCall {
        client.get("$baseUrl/api/v1/search") {
            withPreviewUserId()
            header("Accept-Language", languageToIso639_3(language))
            parameter("page", page)
            parameter("pageSize", pageSize)

            // Add search query if provided
            if (!query.isNullOrBlank()) {
                parameter("q", query)
            }

            // Send multiple tagId parameters for AND logic
            tagIds?.forEach { tagId ->
                parameter("tagId", tagId)
            }

            if (hasVideo != null) {
                parameter("hasVideo", hasVideo)
            }
        }.bodyOrHandle401()
    }

    // ─── Engagement (Likes) ────────────────────────────────────────

    /**
     * Like content (podcast or audiobook)
     * @return true if newly liked, false if already liked (idempotent)
     */
    suspend fun likeContent(
        contentId: String,
        type: String
    ): ApiResult<Boolean> = safeApiCall {
        val response = client.post("$baseUrl/api/v1/likes") {
            withPreviewUserId()
            contentType(ContentType.Application.Json)
            setBody(mapOf("contentId" to contentId, "contentType" to type))
        }
        if (!response.status.isSuccess()) {
            val errorBody = try {
                response.body<com.castly.admin.dto.ErrorResponse>()
            } catch (_: Exception) {
                com.castly.admin.dto.ErrorResponse(
                    error = "HTTP_${response.status.value}",
                    message = "Failed to like content",
                    details = null
                )
            }
            throw Exception("${errorBody.error}: ${errorBody.message}")
        }
        response.status.value == 201 // true if newly liked
    }

    /**
     * Unlike content
     */
    suspend fun unlikeContent(contentId: String): ApiResult<Boolean> = safeApiCall {
        val response = client.delete("$baseUrl/api/v1/likes/$contentId") {
            withPreviewUserId()
        }
        if (!response.status.isSuccess()) {
            val errorBody = try {
                response.body<com.castly.admin.dto.ErrorResponse>()
            } catch (_: Exception) {
                com.castly.admin.dto.ErrorResponse(
                    error = "HTTP_${response.status.value}",
                    message = "Failed to unlike content",
                    details = null
                )
            }
            throw Exception("${errorBody.error}: ${errorBody.message}")
        }
        true
    }

    /**
     * Get liked content list
     */
    suspend fun getLikedContent(
        page: Int = 1,
        pageSize: Int = 20,
        language: String = "en"
    ): ApiResult<com.castly.admin.dto.ContentListResponse> = safeApiCall {
        val response = client.get("$baseUrl/api/v1/likes") {
            withPreviewUserId()
            header("Accept-Language", languageToIso639_3(language))
            parameter("page", page)
            parameter("pageSize", pageSize)
        }
        if (!response.status.isSuccess()) {
            val errorBody = try {
                response.body<com.castly.admin.dto.ErrorResponse>()
            } catch (_: Exception) {
                com.castly.admin.dto.ErrorResponse(
                    error = "HTTP_${response.status.value}",
                    message = "Failed to get likes",
                    details = null
                )
            }
            throw Exception("${errorBody.error}: ${errorBody.message}")
        }
        response.body()
    }

    // ─── Stream Progress / Continue Watching ────────────────────────

    @Serializable
    private data class StreamProgressRequestBody(
        val episodeId: String,
        val positionSeconds: Int,
        val durationSeconds: Int? = null
    )

    /**
     * Report stream progress for an episode
     */
    suspend fun reportStreamProgress(
        episodeId: String,
        positionSeconds: Int,
        durationSeconds: Int? = null
    ): ApiResult<com.castly.admin.dto.StreamProgressResponse> = safeApiCall {
        client.post("$baseUrl/api/v1/stream-progress") {
            withPreviewUserId()
            contentType(ContentType.Application.Json)
            setBody(StreamProgressRequestBody(episodeId, positionSeconds, durationSeconds))
        }.bodyOrHandle401()
    }

    /**
     * Get continue watching list (unfinished streams)
     */
    suspend fun getContinueWatching(
        page: Int = 1,
        pageSize: Int = 20
    ): ApiResult<com.castly.admin.dto.EpisodeListResponse> = safeApiCall {
        client.get("$baseUrl/api/v1/stream-progress") {
            withPreviewUserId()
            parameter("page", page)
            parameter("pageSize", pageSize)
        }.bodyOrHandle401()
    }

    // ─── ONIX Import ───────────────────────────────────────────────

    suspend fun importOnixPreview(file: File): OnixImportPreview {
        val token = window.localStorage.getItem("id_token")
        val formData = FormData()
        formData.append("file", file)

        val response = window.fetch(
            "$baseUrl/api/admin/audiobooks/import/onix",
            RequestInit(
                method = "POST",
                body = formData,
                headers = json("Authorization" to "Bearer $token")
            )
        ).await()

        if (!response.ok) {
            handleFetchResponse(response)
            val errorText = response.text().await()
            throw Exception("ONIX parsing failed: $errorText")
        }

        val jsonText = response.text().await()
        return Json.decodeFromString(jsonText)
    }

    suspend fun confirmOnixImport(request: OnixImportConfirmRequest): ImportReportDto {
        return client.post("$baseUrl/api/admin/audiobooks/import/onix/confirm") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    // ─── ISBN Lookup ───────────────────────────────────────────────

    suspend fun isbnLookup(isbn: String): IsbnLookupResult {
        return client.get("$baseUrl/api/admin/audiobooks/isbn-lookup") {
            withAuth()
            parameter("isbn", isbn)
        }.bodyOrHandle401()
    }

    // ─── RSS Audiobook Import ──────────────────────────────────────

    suspend fun rssAudiobookPreview(rssUrl: String): RssImportPreview {
        return client.post("$baseUrl/api/admin/audiobooks/import/rss") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(RssImportRequest(rssUrl))
        }.bodyOrHandle401()
    }

    suspend fun confirmRssAudiobookImport(request: RssImportConfirmRequest): Map<String, String> {
        return client.post("$baseUrl/api/admin/audiobooks/import/rss/confirm") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    // ─── Download Status ───────────────────────────────────────────

    suspend fun getAudiobookDownloadStatus(audiobookId: String): AudiobookDownloadStatus {
        return client.get("$baseUrl/api/admin/audiobooks/$audiobookId/download-status") {
            withAuth()
        }.bodyOrHandle401()
    }

    // ─── Podcast Conversion ────────────────────────────────────────

    suspend fun convertPodcastToAudiobook(podcastId: String, request: ConvertToAudiobookRequest): ConversionResultDto {
        return client.post("$baseUrl/api/admin/podcasts/$podcastId/convert-to-audiobook") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    // ─── Active Downloads ─────────────────────────────────────────

    suspend fun getActiveDownloads(): ActiveDownloadsResponse {
        return client.get("$baseUrl/api/admin/audiobooks/downloads/active") {
            withAuth()
        }.bodyOrHandle401()
    }

    suspend fun cancelAndClearDownloads(audiobookId: String) {
        client.delete("$baseUrl/api/admin/audiobooks/$audiobookId/downloads") {
            withAuth()
        }.bodyOrHandle401<Map<String, String>>()
    }

    /** Requeue an audiobook's failed and stalled chapter downloads. */
    suspend fun retryAudiobookDownloads(audiobookId: String): RetryDownloadsResponse {
        return client.post("$baseUrl/api/admin/audiobooks/$audiobookId/downloads/retry") {
            withAuth()
        }.bodyOrHandle401()
    }

    suspend fun getImportHistory(limit: Int = 20, offset: Int = 0): ImportHistoryResponse {
        return client.get("$baseUrl/api/admin/import-history") {
            withAuth()
            parameter("limit", limit)
            parameter("offset", offset)
        }.bodyOrHandle401()
    }

    suspend fun getImportLogDetail(id: Int): com.castly.shared.dto.ImportLogDetailDto {
        return client.get("$baseUrl/api/admin/import-history/$id") {
            withAuth()
        }.bodyOrHandle401()
    }

    // ─── ONIX Export ───────────────────────────────────────────────

    suspend fun exportOnix(request: OnixExportRequest): String {
        val response = client.post("$baseUrl/api/admin/audiobooks/export/onix") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (response.status.value == 401) handleSessionExpiration()
        if (!response.status.isSuccess()) {
            throw Exception("Export failed (${response.status.value})")
        }
        return response.bodyAsText()
    }

    // ─── Super Admin: User Management ─────────────────────────────

    suspend fun listAdminUsers(): List<AdminUserInfoDto> {
        return client.get("$baseUrl/api/admin/super/users") { withAuth() }.bodyOrHandle401()
    }

    suspend fun createAdminUser(email: String, groupName: String): Map<String, String> {
        return client.post("$baseUrl/api/admin/super/users") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(mapOf("email" to email, "groupName" to groupName))
        }.bodyOrHandle401()
    }

    suspend fun setUserGroup(email: String, groupName: String): Map<String, String> {
        return client.put("$baseUrl/api/admin/super/users/${email.encodeURLParameter()}/group") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(mapOf("groupName" to groupName))
        }.bodyOrHandle401()
    }

    suspend fun disableUser(email: String): UserStatusDto {
        return client.post("$baseUrl/api/admin/super/users/${email.encodeURLParameter()}/disable") { withAuth() }.bodyOrHandle401()
    }

    suspend fun enableUser(email: String): UserStatusDto {
        return client.post("$baseUrl/api/admin/super/users/${email.encodeURLParameter()}/enable") { withAuth() }.bodyOrHandle401()
    }

    // ─── Super Admin: Permission Management ───────────────────────

    suspend fun getPermissionMatrix(): PermissionMatrixDto {
        return client.get("$baseUrl/api/admin/super/permissions") { withAuth() }.bodyOrHandle401()
    }

    suspend fun updatePermission(groupName: String, vertical: String, permissionLevel: String): PermissionEntryDto {
        return client.put("$baseUrl/api/admin/super/permissions") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(mapOf("groupName" to groupName, "vertical" to vertical, "permissionLevel" to permissionLevel))
        }.bodyOrHandle401()
    }

    suspend fun getMyPermissions(): MyPermissionsDto {
        return client.get("$baseUrl/api/admin/super/my-permissions") { withAuth() }.bodyOrHandle401()
    }

    /**
     * Upload FormData via XMLHttpRequest with real upload progress callback.
     * Returns the response text on success, throws on failure.
     */
    private suspend fun uploadWithProgress(
        url: String,
        method: String,
        formData: FormData,
        onProgress: ((loaded: Double, total: Double) -> Unit)? = null
    ): String = suspendCancellableCoroutine { cont ->
        val token = window.localStorage.getItem("id_token")
        val xhr = XMLHttpRequest()
        xhr.open(method, url)
        xhr.setRequestHeader("Authorization", "Bearer $token")

        xhr.upload.addEventListener("progress", { event ->
            val progressEvent = event.asDynamic()
            if (progressEvent.lengthComputable as Boolean) {
                onProgress?.invoke(
                    progressEvent.loaded as Double,
                    progressEvent.total as Double
                )
            }
        })

        xhr.addEventListener("load", {
            if (xhr.status in 200..299) {
                cont.resume(xhr.responseText)
            } else if (xhr.status.toInt() == 401) {
                window.localStorage.removeItem("id_token")
                window.localStorage.removeItem("user_role")
                window.sessionStorage.setItem("session_expired", "true")
                window.location.reload()
                cont.resumeWithException(SessionExpiredException())
            } else {
                cont.resumeWithException(Exception("Upload failed (${xhr.status}): ${xhr.responseText}"))
            }
        })

        xhr.addEventListener("error", {
            cont.resumeWithException(Exception("Upload failed: network error"))
        })

        xhr.addEventListener("abort", {
            cont.resumeWithException(Exception("Upload cancelled"))
        })

        cont.invokeOnCancellation { xhr.abort() }
        xhr.send(formData)
    }

    // ── Label Management ─────────────────────────────────────────

    suspend fun listLabels(includeInactive: Boolean = false): List<com.castly.shared.dto.LabelDto> {
        return client.get("$baseUrl/api/admin/labels") {
            withAuth()
            if (includeInactive) parameter("includeInactive", "true")
        }.bodyOrHandle401()
    }

    suspend fun createLabel(request: com.castly.shared.dto.CreateLabelRequest): com.castly.shared.dto.LabelDto {
        return client.post("$baseUrl/api/admin/labels") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    suspend fun getLabel(id: Int): com.castly.shared.dto.LabelDetailDto {
        return client.get("$baseUrl/api/admin/labels/$id") { withAuth() }.bodyOrHandle401()
    }

    suspend fun updateLabel(id: Int, request: com.castly.shared.dto.UpdateLabelRequest): com.castly.shared.dto.LabelDto {
        return client.put("$baseUrl/api/admin/labels/$id") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    suspend fun deactivateLabel(id: Int): com.castly.shared.dto.LabelDto {
        return client.post("$baseUrl/api/admin/labels/$id/deactivate") { withAuth() }.bodyOrHandle401()
    }

    suspend fun activateLabel(id: Int): com.castly.shared.dto.LabelDto {
        return client.post("$baseUrl/api/admin/labels/$id/activate") { withAuth() }.bodyOrHandle401()
    }

    // ── Label User Management ────────────────────────────────────

    suspend fun listLabelUsers(labelId: Int): List<com.castly.shared.dto.LabelUserDto> {
        return client.get("$baseUrl/api/admin/labels/$labelId/users") { withAuth() }.bodyOrHandle401()
    }

    suspend fun createLabelUser(labelId: Int, request: com.castly.shared.dto.CreateLabelUserRequest): com.castly.shared.dto.LabelUserDto {
        return client.post("$baseUrl/api/admin/labels/$labelId/users") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrHandle401()
    }

    suspend fun assignUserToLabel(userId: String, labelId: Int): com.castly.shared.dto.LabelUserDto {
        return client.put("$baseUrl/api/admin/labels/users/$userId/assign") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(com.castly.shared.dto.AssignUserToLabelRequest(labelId = labelId))
        }.bodyOrHandle401()
    }

    suspend fun unassignUser(userId: String) {
        client.delete("$baseUrl/api/admin/labels/users/$userId/unassign") { withAuth() }
    }
}

@Serializable
data class AdminUserInfoDto(
    val username: String,
    val email: String,
    val enabled: Boolean,
    val status: String,
    val groups: List<String>,
    val role: String
)

@Serializable
data class UserStatusDto(
    val email: String,
    val enabled: Boolean
)

@Serializable
data class PermissionMatrixDto(
    val permissions: List<PermissionEntryDto>,
    val verticals: List<String>,
    val groups: List<String>
)

@Serializable
data class PermissionEntryDto(
    val groupName: String,
    val vertical: String,
    val permissionLevel: String
)

@Serializable
data class MyPermissionsDto(
    val role: String,
    val fullAccess: Boolean = false,
    val permissions: Map<String, String> = emptyMap(),
    val isLabelAdmin: Boolean = false,
    val userLabelId: Int? = null
)
